/**
 * B300 (founder ruling 2026-09-29: save the device timezone on app open, keep
 * the no-guess rule) — the app-wide home of the timezone stamp.
 *
 * The server's athlete day frame (`api/lib/team/athleteDayFrame.js`) fails
 * closed without `athlete_context.context_data._timezone`: the coach roster
 * and analysis panel blank their day-based fields, team presence skips the
 * athlete, and live race arming refuses. Before this mount only two places
 * wrote the key, and neither ran for everyone: `useProactiveChecks` (Today
 * only, and only when a nudge preference is on and the dashboard has not been
 * visited today) and `useTimezoneStamp` (the guardian home only). An account
 * that never opened Today, or opened it with both nudge families off, never
 * got a zone.
 *
 * This mounts the EXISTING `useTimezoneStamp` once for every signed-in account
 * (athlete, coach, guardian, dual), wherever it lands. It renders nothing and
 * the server rule is untouched: no zone here means no frame there, never a
 * guessed one.
 *
 * Signed out renders nothing at all, so the hook's one attempt per mount is
 * not spent on the signed-out context (whose `mergeContext` is a no-op). The
 * inner component is keyed on the uid, so an in-place account switch starts a
 * fresh attempt for the new account rather than reusing the previous one's.
 *
 * A refused or failed merge is silent. `mergeContext` returns nothing and the
 * provider's own persist path logs a single `console.warn` and drops the
 * batch (an age-gated 403 included); there is no toast and this component adds
 * no retry. The guardian home keeps its own `useTimezoneStamp()` call: two
 * callers landing in the same tick coalesce into one write in the provider's
 * debounce, and a later one finds the zone already matching and does nothing.
 */

import { useAuthContext } from '../contexts/AuthContext';
import { useTimezoneStamp } from '../hooks/useTimezoneStamp';

function TimezoneStamp(): null {
  useTimezoneStamp();
  return null;
}

export function TimezoneStampMount(): JSX.Element | null {
  const { user } = useAuthContext();
  if (!user) return null;
  return <TimezoneStamp key={user.id} />;
}
